import { motion, useIsPresent } from 'framer-motion';
import { rgba } from 'polished';
import { type HTMLAttributes, useRef, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Avatar } from '@treams/treams-ui';
import { type AvatarRingTone, Avatar as AvatarV2, tokens } from '@treams/treams-ui';

import useTalentReviewDropStyle from '../hooks/useTalentReviewDropStyle';
import { useTalentReviewPresence } from '../talentReviewPresence';
import TalentReviewAvatarTooltip from './TalentReviewTooltip';

const FOCUS_EASE = [0.075, 0.82, 0.165, 1] as const;
const FOCUS_TRANSITION = {
	duration: 0.5,
	ease: FOCUS_EASE,
};
/** CSS twin of FOCUS_TRANSITION for the gap a leaving avatar closes (see `Container`). */
const LEAVE_TRANSITION = `margin-right 0.5s cubic-bezier(${FOCUS_EASE.join(', ')})`;

/** Gap between avatars in a cell (see `TalentReviewCell`); a leaving avatar closes it. */
const CELL_GAP = 8;

/** Width of an avatar in a cell. */
const AVATAR_SIZE = 40;

/** Room for one more avatar: the slot a dragged person would take, opened next to a neighbour. */
const SLOT = AVATAR_SIZE + CELL_GAP;

/** Longest random head start (seconds) before an entering avatar grows in. */
const ENTER_DELAY_MAX = 0.4;

/** Hovered avatars pop up slightly, with a little overshoot. */
const HOVER_SCALE = 1.05;
const HOVER_SPRING = { type: 'spring' as const, stiffness: 400, damping: 17 };

const MotionAvatarFrame = styled(motion.div)`
	border-radius: ${tokens.radius.full}px;
	overflow: hidden;
	line-height: 0;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;

/*
 * Sits outside the Draggable so dnd only measures the avatar itself. When lifted, it
 * centers the focused avatar over the whole cell without making the draggable cell-sized
 * (which would produce a cell-sized placeholder that always wraps to a new row).
 */
const Lift = styled.div<{ $lifted?: boolean }>`
	display: contents;
	${(props) =>
		props.$lifted &&
		`
		display: flex;
		align-items: center;
		justify-content: center;
		position: absolute;
		inset: 0;
		z-index: 2;
		pointer-events: none;
	`}
`;

/*
 * The flex item in the cell. While leaving it also closes the cell gap after
 * it, in step with the shrinking avatar, so the neighbours arrive in one move.
 * Hidden (not animated) when the person is shown elsewhere, and taken out of
 * the layout altogether when they were just dropped there; see `TalentReviewPerson`.
 * While someone is dragged over the cell, the avatar at the insertion point
 * opens a slot in front of itself (or, for the last one, behind itself); the
 * layout animation of the avatars moves them there, so the margin itself is
 * not transitioned.
 */
const Container = styled.div<{
	$leaving?: boolean;
	$hidden?: boolean;
	$collapsed?: boolean;
	$slotBefore?: boolean;
	$slotAfter?: boolean;
}>`
	min-height: 0;
	overflow: visible;
	position: relative;
	line-height: 0;
	pointer-events: auto;
	${(props) => props.$leaving && `margin-right: -${CELL_GAP}px;`}
	${(props) => props.$hidden && 'visibility: hidden;'}
	${(props) => props.$collapsed && 'display: none;'}
	${(props) => props.$slotBefore && `margin-left: ${SLOT}px;`}
	${(props) => props.$slotAfter && `margin-right: ${SLOT}px;`}
`;

const AvatarWrap = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;
`;

/*
 * The ring around the avatar. Selected (enlarged): 3px brand. Hovered or
 * highlighted from the sidebar: 2px brand, the same width as the outdated
 * (warning) ring, so that one only changes colour.
 */
const ringTone = (props: {
	focused?: boolean;
	highlighted?: boolean;
	outdated?: boolean;
}): AvatarRingTone | undefined =>
	props.focused || props.highlighted ? 'brand' : props.outdated ? 'warning' : undefined;

const PersonCard = styled(Flex)<{
	dragging?: boolean;
	$readonly?: boolean;
	$dimmed?: boolean;
	$selectable?: boolean;
	$focused?: boolean;
}>`
	position: relative;
	box-sizing: border-box;
	width: auto;
	min-height: 0;
	margin: 0;
	${(props) => props.$focused && 'margin-top: -8px;'}
	padding: 0;
	border-radius: 50%;
	background: transparent;
	pointer-events: auto;
	flex-shrink: 0;
	flex-grow: 0;
	${(props) =>
		props.$focused && !props.$readonly
			? 'cursor: grab;'
			: props.$selectable
				? 'cursor: pointer;'
				: ''}

	/* While someone else is selected: hidden and not interactive. */
	${(props) =>
		props.$dimmed &&
		`
		opacity: 0;
		pointer-events: none;
		transition: opacity 100ms ease-in-out;
	`}

	${(props) =>
		props.dragging &&
		`
		background: white;
		box-shadow: 0px 8px 16px ${rgba(tokens.border.subtle, 0.25)};
		transition: 0.4s ease-out box-shadow, 0.4s ease-out background-color;
	`}
`;

// One avatar in a grid cell: draggable, selectable, and animated between cells.
export default function TalentReviewPerson(props: {
	user: graphql.User;
	index: number;
	readonly?: boolean;
	dragging?: boolean;
	/** Enlarge this person and lift them; used when they are isolated on the grid. */
	focused?: boolean;
	/** Nobody reviewed this placement within the template's threshold. */
	outdated?: boolean;
	/** Hide this person because someone else is isolated. */
	dimmed?: boolean;
	/** Selection ring without isolating, e.g. after a drop or while their sidebar row is hovered. */
	highlighted?: boolean;
	/** Block dragging for everyone except the focused person. */
	dragDisabled?: boolean;
	/** Select this person in the sidebar. */
	onSelect?: () => void;
	/** The droppable this person is rendered in; a drop back into it glides home. */
	droppableId?: string;
	/**
	 * Fires when the avatar starts (true) or finishes (false) a layout animation
	 * towards its cell, so the cell can stop clipping it while it travels.
	 */
	onLayoutAnimation?: (animating: boolean) => void;
	/** Newly on the page: the avatar grows in from the centre instead of gliding from elsewhere. */
	entering?: boolean;
	/** A dragged person would be inserted right before this one: open a slot in front. */
	slotBefore?: boolean;
	/** A dragged person would be appended after this (last) one: open a slot behind. */
	slotAfter?: boolean;
}) {
	const cardRef = useRef<HTMLDivElement>(null);
	const dragWidthRef = useRef<number>();
	const pointerStartRef = useRef<{ x: number; y: number }>();
	// Fixed per avatar, so people entering together (the grid opening) grow in
	// one after another rather than all at once.
	const [enterDelay] = useState(() => Math.random() * ENTER_DELAY_MAX);
	const dropStyle = useTalentReviewDropStyle();
	const isPresent = useIsPresent();
	const presence = useTalentReviewPresence();
	// Leaving while the person is shown elsewhere: the avatar there is the one to
	// look at, so this one is hidden. Plain styles rather than the exit animation:
	// on drag end dnd puts this element back in its old spot in the same render
	// that mounts the new one, and the exit animation only starts after that
	// render has been painted, so the avatar would flash up there for a frame.
	const hidden = !isPresent && presence.present.has(props.user.id);
	// Dropped elsewhere (an exiting element keeps the props of the drag): the
	// neighbours already closed the gap when dnd lifted this element out of the
	// flow, so give up the space at once instead of sliding them back and forth.
	// Being `display: none`, this element also measures 0×0, so the avatar that
	// mounts where the drop landed (sharing this one's layoutId) finds no
	// snapshot to glide from: dnd's drop animation already took it there.
	// Otherwise (a date switch, clearing the grid) the width and gap go over the
	// exit transition, so the neighbours slide along, and the avatar mounting
	// elsewhere glides from this one's spot.
	const collapsed = hidden && !!props.dragging;

	return (
		<Lift $lifted={!!props.focused}>
			<Draggable
				draggableId={props.user.id}
				index={props.index}
				isDragDisabled={props.readonly || props.dragDisabled}
			>
				{(provided, snapshot) => {
					const isDropping = snapshot.isDropAnimating && snapshot.draggingOver?.includes('cell-');
					const dragWidth = snapshot.isDragging && !isDropping ? dragWidthRef.current : undefined;
					// No hover effects, and no shared layout animation, while anything is
					// being dragged. On drag end the avatar mounting where the drop landed
					// takes the layoutId over from the one leaving its old spot; that one
					// is `display: none` by then (see `collapsed`), so there is nothing to
					// glide from and the avatar stays where dnd dropped it.
					const hoverable = !snapshot.isDragging && !props.dragging;
					const shared = hoverable;
					// The avatars around a drag do animate: they make room for the slot
					// and close it again. Not the dragged one (dnd moves it) and not a
					// leaving one (it only gives up its space).
					const animated = !snapshot.isDragging && isPresent;

					return (
						<Container
							$leaving={!isPresent}
							$hidden={hidden}
							$collapsed={collapsed}
							$slotBefore={isPresent && props.slotBefore}
							$slotAfter={isPresent && props.slotAfter}
							ref={provided.innerRef}
							{...provided.draggableProps}
							style={{
								...provided.draggableProps.style,
								// Cells make room with their own slot (a margin) rather than dnd's
								// placeholder, so neighbours take no dnd transform. Only a leaving
								// avatar transitions (its margin), not dnd's transform one.
								...(!snapshot.isDragging
									? {
											transform: 'none',
											transition: !isPresent && !collapsed ? LEAVE_TRANSITION : 'none',
										}
									: {}),
								// Dropping into a cell: land on the sorted spot, where the person
								// is rendered on drag end.
								...dropStyle(provided, snapshot, props.droppableId, props.focused),
							}}
						>
							<motion.div
								layout={animated ? 'position' : false}
								layoutId={shared ? `talent-review-person-${props.user.id}` : undefined}
								// The avatar taking over from a dropped or cleared one must not fade in.
								layoutCrossfade={false}
								transition={FOCUS_TRANSITION}
								onLayoutAnimationStart={() => props.onLayoutAnimation?.(true)}
								onLayoutAnimationComplete={() => props.onLayoutAnimation?.(false)}
								// Grow in when the person is new on the page; shrink away when they
								// leave it. Someone still present elsewhere glides there instead
								// (shared layout), so this instance is hidden (see `Container`) and
								// only gives up its space. Either way the leaving avatar loses its
								// width (and the container its gap) over the same transition, so the
								// neighbours slide along with it instead of after it is gone.
								initial={props.entering ? { scale: 0 } : false}
								animate={{
									scale: 1,
									transition: { ...FOCUS_TRANSITION, delay: props.entering ? enterDelay : 0 },
								}}
								exit="exit"
								variants={{
									exit: (present?: ReadonlySet<string>) =>
										present?.has(props.user.id) ? { width: 0 } : { scale: 0, width: 0 },
								}}
								style={{
									display: 'flex',
									lineHeight: 0,
									originX: 0.5,
									originY: 0.5,
								}}
							>
								<PersonCard
									ref={cardRef}
									dragging={snapshot.isDragging}
									$readonly={props.readonly}
									$dimmed={!!props.dimmed && !snapshot.isDragging}
									$selectable={!!props.onSelect}
									$focused={!!props.focused}
									alignItems="center"
									style={dragWidth ? { width: dragWidth } : undefined}
									onPointerDownCapture={(event) => {
										dragWidthRef.current = cardRef.current?.offsetWidth;
										pointerStartRef.current = { x: event.clientX, y: event.clientY };
									}}
									{...provided.dragHandleProps}
									onMouseUp={(event) => {
										if (snapshot.isDragging) {
											return;
										}
										const start = pointerStartRef.current;
										pointerStartRef.current = undefined;
										if (
											start &&
											(Math.abs(event.clientX - start.x) > 5 ||
												Math.abs(event.clientY - start.y) > 5)
										) {
											return;
										}
										props.onSelect?.();
									}}
									onKeyDown={(event) => {
										(provided.dragHandleProps as HTMLAttributes<HTMLElement> | null)?.onKeyDown?.(
											event,
										);
										if (
											!props.onSelect ||
											snapshot.isDragging ||
											(!props.focused && !props.dragDisabled) ||
											(event.key !== 'Enter' && event.key !== ' ')
										) {
											return;
										}
										event.preventDefault();
										props.onSelect();
									}}
								>
									<AvatarWrap>
										<TalentReviewAvatarTooltip
											user={props.user}
											enabled={!snapshot.isDragging && !props.dragging}
										>
											<AvatarV2.Ring
												tone={ringTone(props)}
												width={props.focused ? 3 : 2}
												hoverTone={hoverable ? 'brand' : undefined}
												hoverWidth={props.focused ? 3 : 2}
												initial={false}
												animate={{ scale: hoverable && props.highlighted ? HOVER_SCALE : 1 }}
												whileHover={hoverable ? { scale: HOVER_SCALE } : undefined}
												transition={{ scale: HOVER_SPRING }}
											>
												<MotionAvatarFrame
													initial={false}
													animate={{
														width: props.focused ? 96 : 40,
														height: props.focused ? 96 : 40,
													}}
													transition={FOCUS_TRANSITION}
												>
													<motion.div
														initial={false}
														animate={{ scale: (props.focused ? 96 : 40) / 96 }}
														transition={FOCUS_TRANSITION}
														style={{ width: 96, height: 96, flexShrink: 0 }}
													>
														<Avatar size={96} user={props.user} />
													</motion.div>
												</MotionAvatarFrame>
											</AvatarV2.Ring>
										</TalentReviewAvatarTooltip>
									</AvatarWrap>
								</PersonCard>
							</motion.div>
						</Container>
					);
				}}
			</Draggable>
		</Lift>
	);
}
